import { html, PolymerElement } from '@polymer/polymer';
import { GestureEventListeners } from '@polymer/polymer/lib/mixins/gesture-event-listeners.js';
import '@polymer/polymer/lib/elements/dom-if.js';
import '@wdpr/typography';
import '@wdpr/color';

import './style/wdpr-button-style.js';


class WdprButton extends GestureEventListeners(PolymerElement) {

  constructor() {
    super();
    this._click = this.click
 }

  static get is() {
    return 'wdpr-button';
  }

  static get template() {
    return html`
  <style include="wdpr-typography"></style>
  <style include="wdpr-color"></style>
  <style include="wdpr-button-style"></style>
  <slot></slot>
  `;
  }

  static get properties() {
    return {
      /**
      * Theme of button
      */
      theme: {
        type: String,
        value: 'primary',
        reflectToAttribute: true
      },
      /**
      * Size of button
      */
      size: {
        type: String,
        value: 'standard',
        reflectToAttribute: true
      },
      /**
      * Button role
      */
      role: {
        type: String,
        value: 'button',
        reflectToAttribute: true
      },
      tabindex: {
        type: Number,
        value: undefined,
        reflectToAttribute: true
      },
      /**
      * Tab index when the button is not disabled
      * For accessibility reasons, it is set to the value
      * of tabindex upon component load to avoid breaking changes.
      */
      activeTabIndex: {
        type: String,
        value: '0'
      },
      /**
      * If button is in pressed state
      */
      pressed: {
        type: Boolean,
        value: false,
        reflectToAttribute: true
      },
      /**
      * If button is disabled
      */
      disabled: {
        type: Boolean,
        value: false,
        notify: true,
        reflectToAttribute: true,
        observed: '_watchHandler'
      },
      /**
      * If button is in active state
      */
      active: {
        type: Boolean,
        value: false,
        notify: true,
        reflectToAttribute: true,
        observed: '_activeHandler'
      },
      /**
      * True if the element is currently being pressed by a "pointer," which
      * is loosely defined as mouse or touch input (but specifically excluding
      * keyboard input).
      */
      pointerDown: {
        type: Boolean,
        value: false
      },
      /**
      * True if the input device that caused the element to receive focus
      * was a keyboard.
      */
      receivedFocusFromKeyboard: {
        type: Boolean,
        value: true,
      },
      /**
      * True if the element is focused
      */
      focused: {
        type: Boolean,
        value: false,
        notify: true,
        reflectToAttribute: true,
        observed : '_watchFocused'
      },
      /**
      * Value of the aria active attribute, set to true or false
      */
      ariaActiveAttribute: {
        type: String,
        value: 'aria-pressed',
        observed: '_ariaActiveHandler',
      },
      /**
      * If true, the button toggles the active state with each tap or press
      * of the spacebar.
      */
      toggles: {
        type: Boolean,
        value: false,
        reflectToAttribute: true
      },
    };
  }

  ready() {
    super.ready();
    this.addEventListener('keydown', this._handleSpaceDown.bind(this));
    this.addEventListener('keyup', this._handleSpaceUp.bind(this));
    this.addEventListener('mousedown', this._handleMouseDown.bind(this));
    this.addEventListener('mouseup', this._handleMouseUp.bind(this));
    this.addEventListener('click', this._clickHandler.bind(this));
    if (this.hasAttribute('tabindex')) {
      this.activeTabIndex = this.getAttribute('tabindex');
    }
    else {
        this.setAttribute('tabindex', this.disabled ? '-1' : '0');
    }
    this.setAttribute('aria-disabled', this.disabled ? 'true' : 'false');
  }

  /**
   * Watcher when active prop changes
   * @param newValue {boolean}
   */
  _activeHandler(newValue) {
    this._activeChanged(newValue, this.ariaActiveAttribute);
    this.dispatchActive.emit(newValue);
  }

  /**
   * Watcher when ariaActiveAttribute changes
   * @param newValue {string}
   * @param oldValue {string}
   */
  _ariaActiveHandler(newValue, oldValue) {
    this._ariaActiveChanged(newValue, oldValue);
    this._activeChanged(this.active, newValue);
  }

  /**
   * Removes previous ariaActiveAttribute class if needed.
   * @param newValue {string}
   * @param oldValue {string}
   */
  _ariaActiveChanged(newValue, oldValue) {
    if (oldValue != undefined && oldValue != newValue && this.hasAttribute(oldValue)) {
        this.removeAttribute(oldValue);
    }
  }
  /**
   * Adds attribute for ariaActive class with the value of active.
   * Fired upon changes to active or ariaActiveAttribute attributes.
   * @param newActive {boolean}
   * @param newAriaLabel {string}
   */
  _activeChanged(newActive, newAriaLabel) {
    if (this.toggles) {
        this.setAttribute(newAriaLabel, newActive ? 'true' : 'false');
    } else {
        this.removeAttribute(newAriaLabel);
    }
  }

  /**
   * _disabledChanged is fired when disabled prop is changed
   * @param newValue {boolean}
   * @param oldValue {boolean}
   */
  _watchHandler(newValue, oldValue) {
    this._disabledChanged(newValue, oldValue);
  }

  /**
   * _focusedChanged is fired when focused prop is changed
   * @param newValue {boolean}
   * @param oldValue {boolean}
   */
  _watchFocused(newValue, oldValue) {
    this._focusChanged(newValue, oldValue);
  }

  _focusChanged(newValue, oldValue) {
    this._detectKeyboardFocus(newValue);
    if (!newValue) {
        this.pressed = false;
    }
    if (newValue != oldValue) {
        this.dispatchFocus.emit(newValue);
    }
  }

  /**
   * @private
   * Sets receivedFocusFromKeyboard to true if it did not receive focus from a pointer event
   * @param focused {boolean}
   */
  _detectKeyboardFocus(focused) {
    this.receivedFocusFromKeyboard = (!this.pointerDown && focused);
  }

  /**
   * Presses element when space key is pressed down
   */
  _handleSpaceDown(e) {
    if (this.disabled) {
        return;
    }
    if (e.keyCode === 32) {
        this.pressed = true;
        e.preventDefault();
    }
  }

  /**
   * Fires click() event and ends button press when space or enter keyup
   */
  _handleSpaceUp(e) {
    if (this.disabled) {
        return (false)
    }
    if (e.keyCode === 32) {
        this.pressed = false;
        this.click();
        e.preventDefault();
    }
    else if (e.keyCode == 13) {
        this.click();
    }
  }

  /**
   * Handles mousedown on element
   */
  _handleMouseDown() {
    this.pointerDown = true;
    if (this.disabled) {
        return;
    }
    else {
        this.pressed = true;
    }
  }

  /**
   * Handles mouseup on element
   */
  _handleMouseUp() {
    this.pointerDown = false;
    if (this.disabled) {
        return (false)
    }
    else {
        this.pressed = false;
    }
  }

  /**
   * Handles click on an element
   */
  _clickHandler() {
    if (this.toggles) {
        // a click is needed to toggle the active state
        this._userActivate(!this.active);
    } else {
        this.active = false;
    }
  }

  /**
   * Toggles the active state
   * @param active 
   */
  _userActivate(active) {
    if (this.active !== active) {
        this.active = active;
    }
  }

  /**
   * @private
   * On blur or focused, sets focused property accordingly
   * @param event {Event}
   */
  _focusBlurHandler(event) {
    this.focused = (event.type === 'focus');
  }

  /**
   * @private
   * Toggles tabindex, aria-disabled, and pointer events when disabled is changed
   * @param disabled {boolean}
   * @param _old {boolean}
   */
  _disabledChanged(disabled, old) {
    this.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    this.style.pointerEvents = disabled ? 'none' : '';
    if (disabled) {
        this.pressed = false;
        this.activeTabIndex = this.getAttribute('tabindex');
        this.setAttribute('tabindex', '-1');
        this.focused = false;
        this.blur();
    } else {
        this.setAttribute('tabindex', this.activeTabIndex);
    }
    if (disabled != old) {
        this.dispatchDisabled.emit(disabled);
    }
  }

}

window.customElements.define(WdprButton.is, WdprButton);
